import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, Zap, ShieldCheck, Truck, Bell, Video } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { QtyStepper } from "@/components/ProductCard";
import ProductCard from "@/components/ProductCard";
import ProductReviews from "@/components/ProductReviews";
import { useRealtime } from "@/context/RealtimeContext";

const FALLBACK_IMG = "/rani-brand.png";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [watching, setWatching] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const { qtyOf, add, setQty, setOpen } = useCart();
  const { subscribe } = useRealtime();

  const load = async () => {
    const { data } = await api.get(`/products/${id}`);
    setP(data);
    api.get(`/products?category_id=${data.category_id}&limit=12`).then((r) => setRelated(r.data.filter((x) => x.id !== id).slice(0, 6)));
    api.get(`/products/${id}/watch-price`).then((r) => setWatching(r.data.watching)).catch(() => {});
  };
  useEffect(() => { window.scrollTo(0, 0); load(); }, [id]);
  useEffect(() => subscribe((m) => { if (m.payload?.product_id === id) load(); }), [subscribe, id]);

  useEffect(() => {
    if (!p) return;
    const url = `${window.location.origin}/product/${p.slug || p.id}`;
    const prevTitle = document.title;
    document.title = `${p.name} - ${p.unit} | Rani Super Market`;
    const nodes = [];
    const add = (tag, attrs) => { const el = document.createElement(tag); Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v)); el.setAttribute("data-seo", "1"); document.head.appendChild(el); nodes.push(el); };
    add("meta", { name: "description", content: (p.description || p.name).slice(0, 160) });
    add("link", { rel: "canonical", href: url });
    add("meta", { property: "og:title", content: p.name });
    add("meta", { property: "og:type", content: "product" });
    if (p.image) add("meta", { property: "og:image", content: p.image });
    const ld = document.createElement("script"); ld.type = "application/ld+json"; ld.setAttribute("data-seo", "1");
    ld.text = JSON.stringify({ "@context": "https://schema.org", "@type": "Product", name: p.name, image: p.image, description: p.description, sku: p.sku, brand: { "@type": "Brand", name: p.brand || "Rani Super Market" }, offers: { "@type": "Offer", priceCurrency: "INR", price: p.price, availability: p.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock", url } });
    document.head.appendChild(ld); nodes.push(ld);
    return () => { document.title = prevTitle; nodes.forEach((n) => n.remove()); };
  }, [p]);

  const notifyMe = async () => {
    try { await api.post(`/products/${id}/notify-me`); toast.success("We'll notify you when it's back in stock"); }
    catch (err) { if (err?.response?.status === 401) nav("/login"); else toast.error("Could not register"); }
  };

  const toggleWatch = async () => {
    try {
      if (watching) { await api.delete(`/products/${id}/watch-price`); setWatching(false); toast.success("Price watch off"); }
      else { await api.post(`/products/${id}/watch-price`); setWatching(true); toast.success("Price girte hi hum aapko batayenge!"); }
    } catch (err) { if (err?.response?.status === 401) nav("/login"); else toast.error("Try again"); }
  };

  if (!p) return <div className="max-w-5xl mx-auto p-10 text-slate-400">Loading…</div>;
  const qty = qtyOf(p.id);
  const off = p.mrp > p.price ? Math.round((1 - p.price / p.mrp) * 100) : 0;
  const imgs = (p.images && p.images.length ? p.images : (p.image ? [p.image] : []));
  const curImg = imgs[activeImg] || FALLBACK_IMG;
  const isRealImg = curImg !== FALLBACK_IMG;

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-5">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-slate-500 hover:text-slate-800 text-sm font-medium mb-4"><ChevronLeft size={16} /> Back</button>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <div
            data-testid="product-image-zoombox"
            onClick={() => isRealImg && setLightbox(true)}
            className={`aspect-square rounded-2xl overflow-hidden bg-slate-50 flex items-center justify-center p-4 relative ${isRealImg ? "cursor-zoom-in" : ""}`}
          >
            <img
              data-testid="product-detail-image"
              src={curImg}
              alt={p.name}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_IMG; }}
              className={`w-full h-full ${isRealImg ? "object-cover" : "object-contain p-4 opacity-85"}`}
            />
            {isRealImg && <span className="absolute bottom-2 right-2 bg-black/50 text-white text-[10px] font-bold px-2 py-1 rounded-md">Tap to view full</span>}
          </div>
          {imgs.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar" data-testid="image-gallery">
              {imgs.map((u, i) => (
                <button key={u} data-testid={`gallery-thumb-${i}`} onClick={() => setActiveImg(i)}
                  className={`w-16 h-16 rounded-xl overflow-hidden border-2 shrink-0 bg-slate-50 transition-colors ${i === activeImg ? "border-emerald-500" : "border-transparent hover:border-slate-300"}`}>
                  <img src={u} alt={`${p.name} ${i + 1}`} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_IMG; }} />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 text-xs font-bold rounded-full px-3 py-1"><Zap size={11} /> 10 MIN DELIVERY</span>
          <h1 data-testid="product-detail-name" className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-3">{p.name}</h1>
          <p className="text-slate-500 mt-1">{p.unit} · {p.brand}</p>
          <div className="flex items-center gap-3 mt-4">
            <span className="text-3xl font-extrabold">{inr(p.price)}</span>
            {off > 0 && <><span className="text-lg text-slate-400 line-through">{inr(p.mrp)}</span><span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-md">{off}% OFF</span></>}
          </div>
          <p className="text-xs text-slate-400 mt-1">Inclusive of {p.tax_rate}% GST</p>
          <div className="mt-4">
            {p.in_stock
              ? <span data-testid="stock-status" className="text-sm font-bold text-emerald-600">In stock ({p.stock} available)</span>
              : <span data-testid="stock-status" className="text-sm font-bold text-red-500">Out of stock</span>}
          </div>
          <div className="mt-6 flex gap-3">
            {p.in_stock ? (qty > 0 ? (
              <div className="w-40"><QtyStepper testid={`detail-stepper-${p.id}`} qty={qty} onInc={() => setQty(p.id, qty + 1)} onDec={() => setQty(p.id, qty - 1)} /></div>
            ) : (
              <button data-testid="detail-add-to-cart" onClick={() => add(p)} className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-bold transition-colors">Add to Cart</button>
            )) : (
              <button data-testid="detail-notify-me" onClick={notifyMe} className="flex-1 py-3 border-2 border-emerald-600 text-emerald-700 rounded-2xl font-bold hover:bg-emerald-50 transition-colors">Notify me when available</button>
            )}
            {qty > 0 && <button data-testid="detail-view-cart" onClick={() => setOpen(true)} className="flex-1 py-3 border-2 border-emerald-600 text-emerald-700 rounded-2xl font-bold hover:bg-emerald-50 transition-colors">View Cart</button>}
          </div>
          {p.in_stock && (
            <button data-testid="watch-price-btn" onClick={toggleWatch} className={`mt-3 text-sm font-semibold inline-flex items-center gap-1.5 hover:underline ${watching ? "text-amber-600" : "text-emerald-700"}`}>
              <Bell size={15} className={watching ? "fill-amber-400 text-amber-500" : ""} /> {watching ? "Watching price — turn off" : "Price girne par alert paayein"}
            </button>
          )}
          <p className="text-sm text-slate-600 leading-relaxed mt-6">{p.description}</p>
          <div className="grid grid-cols-3 gap-3 mt-6 text-center">
            {[[Truck,"Fast delivery"],[ShieldCheck,"Quality assured"],[Zap,"Best prices"]].map(([Ic,t])=>(
              <div key={t} className="bg-slate-50 rounded-xl py-3"><Ic size={18} className="mx-auto text-emerald-600" /><p className="text-[11px] font-semibold text-slate-600 mt-1">{t}</p></div>
            ))}
          </div>
        </div>
      </div>
      {p.videos?.length > 0 && (
        <section className="mt-10" data-testid="product-videos">
          <h2 className="text-xl font-extrabold text-slate-900 mb-4 flex items-center gap-2"><Video className="text-blue-600" size={22} /> Product Videos</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {p.videos.map((v, i) => (
              <div key={v.url || i} data-testid={`product-video-${i}`} className="bg-black rounded-2xl overflow-hidden border border-slate-200">
                <video controls preload="metadata" className="w-full max-h-72" src={`${BACKEND}${v.url}`} />
                {v.title && <div className="bg-white px-3 py-2 text-sm font-semibold text-slate-700 truncate">{v.title}</div>}
              </div>
            ))}
          </div>
        </section>
      )}
      <ProductReviews productId={p.id} />
      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="text-xl font-bold mb-4">You may also like</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
            {related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </section>
      )}
      {lightbox && (
        <div data-testid="image-lightbox" className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => setLightbox(false)}>
          <button data-testid="lightbox-close" onClick={() => setLightbox(false)} className="absolute top-4 right-4 z-10 text-white bg-white/10 hover:bg-white/20 rounded-full p-2.5 transition-colors"><X size={26} /></button>
          {imgs.length > 1 && (
            <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i - 1 + imgs.length) % imgs.length); }} className="absolute left-3 z-10 text-white bg-white/10 hover:bg-white/20 rounded-full p-2.5 transition-colors"><ChevronLeft size={28} /></button>
          )}
          <img data-testid="lightbox-image" src={curImg} alt={p.name} onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full object-contain select-none" />
          {imgs.length > 1 && (
            <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i + 1) % imgs.length); }} className="absolute right-3 z-10 text-white bg-white/10 hover:bg-white/20 rounded-full p-2.5 transition-colors"><ChevronRight size={28} /></button>
          )}
          {imgs.length > 1 && <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm font-semibold">{activeImg + 1} / {imgs.length}</span>}
        </div>
      )}
    </div>
  );
}
